/**
 * Arrival day — the staff command dashboard.
 *
 * Expected vs arrived, what each desk is holding, the live feed of arrivals,
 * and a search that resolves any student's checklist on demand. Manual
 * override is only rendered for a holder of `arrival.manage` (the API enforces
 * it regardless).
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Users, UserCheck, AlertTriangle, Ticket, Search, ExternalLink, ShieldCheck,
  CheckCircle2, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';

const Kpi = ({ label, value, icon: Icon, testid, tone = 'text-slate-900' }) => (
  <div className="rounded-lg border bg-white px-3 py-2" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
      <Icon className="h-3 w-3" /> {label}
    </p>
    <p className={`text-2xl font-semibold ${tone}`}>{value}</p>
  </div>
);

const StudentDialog = ({ tenantId, studentId, onClose }) => {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const { data } = useQuery({
    queryKey: ['arrival-student', tenantId, studentId],
    queryFn: async () => (await api.get(
      `/school/arrival/tenants/${tenantId}/students/${studentId}`)).data,
    enabled: !!studentId,
  });

  const override = useMutation({
    mutationFn: (steps) => api.post(
      `/school/arrival/tenants/${tenantId}/students/${studentId}/override`,
      { steps, note }),
    onSuccess: () => {
      toast.success('Cleared at the desk');
      setNote('');
      qc.invalidateQueries({ queryKey: ['arrival-student', tenantId, studentId] });
      qc.invalidateQueries({ queryKey: ['arrival-dashboard', tenantId] });
      qc.invalidateQueries({ queryKey: ['arrival-search', tenantId] });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not clear that')),
  });

  return (
    <Dialog open={!!studentId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"
                     data-testid="arrival-student-dialog">
        <DialogHeader>
          <DialogTitle>
            {data?.student?.name || 'Student'}
            <span className="ml-2 text-xs font-normal text-slate-500">
              {data?.student?.student_number}
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Read live from Finance, Admissions, Housing and the Registry. Clearing an
            item by hand is recorded against your name.
          </DialogDescription>
        </DialogHeader>
        {!data ? <Skeleton className="h-40 w-full" /> : (
          <div className="space-y-4">
            {!!data.tickets?.length && (
              <div className="flex gap-2 flex-wrap" data-testid="arrival-student-tickets">
                {data.tickets.map((t) => (
                  <Badge key={t.id} variant="outline" className="text-xs">
                    <Ticket className="h-3 w-3 mr-1" /> {t.number} · {t.desk_label} · {t.status}
                  </Badge>
                ))}
              </div>
            )}
            <div className="space-y-2">
              {data.items.map((item) => (
                <div key={item.key}
                     className="flex items-start justify-between gap-3 rounded-md border px-3 py-2"
                     data-testid={`arrival-detail-${item.key}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">{item.label}</p>
                    <p className="text-xs text-slate-500">{item.detail || item.source}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={item.status === 'cleared'
                      ? 'bg-emerald-100 text-emerald-700'
                      : item.status === 'not_required' ? 'bg-slate-100 text-slate-600'
                      : 'bg-amber-100 text-amber-800'}>
                      {item.status === 'not_required' ? 'n/a' : item.status}
                    </Badge>
                    {item.status === 'pending' && (
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                              disabled={override.isPending}
                              data-testid={`arrival-override-${item.key}`}
                              onClick={() => override.mutate([item.key])}>
                        <ShieldCheck className="h-3 w-3 mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
                   placeholder="Why are you clearing this by hand? (recorded)"
                   data-testid="arrival-override-note" className="text-sm" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ArrivalDayPanel = ({ tenantId }) => {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['arrival-dashboard', tenantId],
    queryFn: async () => (await api.get(
      `/school/arrival/tenants/${tenantId}/dashboard`)).data,
    refetchInterval: 15000,
  });

  const { data: search } = useQuery({
    queryKey: ['arrival-search', tenantId, q],
    queryFn: async () => (await api.get(
      `/school/arrival/tenants/${tenantId}/students`, { params: { q } })).data,
  });

  const queueAction = useMutation({
    mutationFn: ({ id, action }) => api.post(
      `/school/arrival/tenants/${tenantId}/queue/${id}/${action}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['arrival-dashboard', tenantId] }),
    onError: (e) => toast.error(getErrorMessage(e, 'Could not update that ticket')),
  });

  if (isLoading || !data) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-5" data-testid="arrival-day-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-slate-500">
          Live from Finance, Admissions, Housing and the Registry — nothing here is a
          stored copy. {data.day}
        </p>
        <a href="/arrival/kiosk" target="_blank" rel="noreferrer"
           data-testid="arrival-open-kiosk"
           className="text-xs font-medium text-slate-700 hover:text-slate-900 flex items-center gap-1">
          Open the kiosk <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Expected" value={data.expected} icon={Users} testid="arrival-kpi-expected" />
        <Kpi label="Arrived" value={data.arrived} icon={UserCheck} testid="arrival-kpi-arrived" />
        <Kpi label="Not arrived" value={data.not_arrived} icon={Users}
             testid="arrival-kpi-not-arrived" />
        <Kpi label="Fully cleared" value={data.fully_cleared} icon={CheckCircle2}
             tone="text-emerald-700" testid="arrival-kpi-cleared" />
        <Kpi label="Needs attention" value={data.needs_attention} icon={AlertTriangle}
             tone="text-amber-700" testid="arrival-kpi-attention" />
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="arrival-desks">
        {data.desks.map((desk) => (
          <div key={desk.key} className="rounded-lg border bg-white p-3"
               data-testid={`arrival-desk-card-${desk.key}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-slate-900">{desk.label}</p>
                <p className="text-[11px] text-slate-500 flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {desk.location}
                </p>
              </div>
              <p className="text-2xl font-bold text-slate-900"
                 data-testid={`arrival-desk-waiting-${desk.key}`}>{desk.waiting}</p>
            </div>
            <p className="mt-1 text-[11px] text-slate-500">
              {desk.serving} being served · {desk.done} done today
            </p>
            <div className="mt-2 space-y-1">
              {desk.queue.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-2 text-xs
                                           rounded border px-2 py-1">
                  <span className="font-mono font-semibold">{t.number}</span>
                  <span className="truncate flex-1 text-slate-600">{t.student_name}</span>
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]"
                          data-testid={`arrival-queue-${t.id}`}
                          onClick={() => queueAction.mutate({
                            id: t.id, action: t.status === 'waiting' ? 'serve' : 'close' })}>
                    {t.status === 'waiting' ? 'Serve' : 'Close'}
                  </Button>
                </div>
              ))}
              {!desk.queue.length && (
                <p className="text-[11px] text-slate-400">Nobody waiting.</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border bg-white p-3">
          <p className="text-sm font-semibold text-slate-900">Live arrivals</p>
          <div className="mt-2 space-y-1 max-h-72 overflow-y-auto" data-testid="arrival-feed">
            {data.feed.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-2 text-xs
                                         border-b py-1.5 last:border-0">
                <span className="font-medium text-slate-800 truncate">{v.student_name}</span>
                <span className="text-slate-500 truncate flex-1">
                  {v.outcome === 'cleared' ? 'all clear'
                    : `sent to ${v.pending_desks.join(', ')}`}
                </span>
                <span className="text-slate-400">{(v.at || '').slice(11, 16)}</span>
              </div>
            ))}
            {!data.feed.length && (
              <p className="text-xs text-slate-400">No arrivals recorded yet.</p>
            )}
          </div>
        </div>

        <div className="rounded-lg border bg-white p-3">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search by name or student number"
                   data-testid="arrival-search-input" className="h-8 text-sm" />
          </div>
          <div className="mt-2 space-y-1 max-h-72 overflow-y-auto"
               data-testid="arrival-search-results">
            {(search?.items || []).map((row) => (
              <button key={row.student.id} onClick={() => setOpenId(row.student.id)}
                      data-testid={`arrival-search-row-${row.student.id}`}
                      className="w-full text-left flex items-center justify-between gap-2
                                 text-xs border-b py-1.5 last:border-0 hover:bg-slate-50">
                <span className="font-medium text-slate-800 truncate">
                  {row.student.name}
                </span>
                <span className="text-slate-400 truncate">{row.student.student_number}</span>
                <Badge className={row.cleared ? 'bg-emerald-100 text-emerald-700'
                                              : 'bg-amber-100 text-amber-800'}>
                  {row.cleared ? 'clear' : `${row.outstanding.length} pending`}
                </Badge>
              </button>
            ))}
            {!(search?.items || []).length && (
              <p className="text-xs text-slate-400">No students match that.</p>
            )}
          </div>
        </div>
      </div>

      {openId && (
        <StudentDialog tenantId={tenantId} studentId={openId}
                       onClose={() => setOpenId(null)} />
      )}
    </div>
  );
};

export default ArrivalDayPanel;
